import React from 'react';
import { Box, Container, Divider, Grid, Link as MuiLink, Stack, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { regionName } from '../../utils/utils';
import { brand } from '../../theme';
import { CONTACT_EMAIL } from '../../common/contact';

const LOCATIONS = ['it', 'gb', 'us', 'de', 'fr', 'es'];

const ROLES = [
    { label: 'Java Developer', path: '/jobs/java-developer' },
    { label: 'Frontend Developer', path: '/jobs/frontend-developer' },
    { label: 'Backend Developer', path: '/jobs/backend-developer' },
    { label: 'Fullstack Developer', path: '/jobs/fullstack-developer' },
    { label: 'Data Analyst', path: '/jobs/data-analyst' },
    { label: 'DevOps Engineer', path: '/jobs/devops-engineer' },
    { label: 'Project Manager', path: '/jobs/project-manager' }
];

const COMBINATIONS = [
    { role: 'Developer', tag: 'it', path: '/jobs/developer/in-it' },
    { role: 'Data Analyst', tag: 'gb', path: '/jobs/data-analyst/in-gb' },
    { role: 'Project Manager', tag: 'de', path: '/jobs/project-manager/in-de' },
    { role: 'Fullstack Developer', tag: 'es', path: '/jobs/fullstack-developer/in-es' },
    { role: 'Backend Developer', tag: 'fr', path: '/jobs/backend-developer/in-fr' }
];

const linkSx = {
    color: 'text.secondary',
    fontSize: '0.92rem',
    fontWeight: 500,
    py: 0.4,
    display: 'inline-block',
    transition: 'color .2s ease, transform .2s ease',
    '&:hover': { color: 'primary.main', transform: 'translateX(3px)' },
};

function Column({ icon, title, items }) {
    return (
        <Grid item xs={12} sm={6} md={4}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                {icon}
                <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 800 }}>{title}</Typography>
            </Stack>
            <Stack>
                {items.map((item) => (
                    <MuiLink key={item.path} component={Link} to={item.path} underline="none" sx={linkSx}>
                        {item.label}
                    </MuiLink>
                ))}
            </Stack>
        </Grid>
    );
}

export default function FooterComponent() {
    const { t, i18n } = useTranslation();
    const lang = i18n.resolvedLanguage || i18n.language || 'en';

    const locations = [
        ...LOCATIONS.map((tag) => ({ label: t('footer.jobsIn', { place: regionName(tag, lang) }), path: `/jobs/in-${tag}` })),
        { label: t('footer.remoteWorldwide'), path: '/jobs/remote' },
    ];
    const combos = COMBINATIONS.map((c) => ({ label: t('jobs.roleInPlace', { role: c.role, place: regionName(c.tag, lang) }), path: c.path }));

    return (
        <Box component="footer" sx={{ backgroundColor: '#FFFFFF', borderTop: `1px solid ${brand.border}`, pt: { xs: 5, md: 7 }, pb: 4, mt: 'auto' }}>
            <Container maxWidth="lg">
                <Grid container spacing={4} sx={{ mb: 4 }}>
                    <Column icon={<PlaceOutlinedIcon color="primary" fontSize="small" />} title={t('footer.browseByLocation')} items={locations} />
                    <Column icon={<WorkOutlineIcon color="primary" fontSize="small" />} title={t('footer.browseByRole')} items={ROLES} />
                    <Column icon={<ExploreOutlinedIcon color="primary" fontSize="small" />} title={t('footer.featuredSearches')} items={combos} />
                </Grid>

                <Divider sx={{ my: 3 }} />

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ md: 'center' }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box component="img" src="/img/logo-96.png" alt="" width={32} height={32} sx={{ borderRadius: '9px' }} />
                        <Typography sx={{ fontWeight: 800 }}>PlaceYourJob</Typography>
                        <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                            &copy; {new Date().getFullYear()} {t('footer.allRightsReserved')}
                        </Typography>
                    </Stack>
                    <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                        <MuiLink component={Link} to="/posts" underline="none" sx={{ ...linkSx, fontWeight: 700 }}>{t('footer.jobs')}</MuiLink>
                        <MuiLink component={Link} to="/your-posts" underline="none" sx={{ ...linkSx, fontWeight: 700 }}>{t('footer.yourJobs')}</MuiLink>
                        <MuiLink component={Link} to="/favourites" underline="none" sx={{ ...linkSx, fontWeight: 700 }}>{t('footer.favourites')}</MuiLink>
                    </Stack>
                </Stack>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 1.5, md: 3 }} justifyContent="space-between" alignItems={{ md: 'center' }} sx={{ mt: 3 }}>
                    <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                        <MuiLink component={Link} to="/privacy" underline="none" sx={linkSx}>{t('footer.privacy')}</MuiLink>
                        <MuiLink component={Link} to="/terms" underline="none" sx={linkSx}>{t('footer.terms')}</MuiLink>
                        <MuiLink href={`mailto:${CONTACT_EMAIL}`} underline="none" sx={{ ...linkSx, display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                            <MailOutlineIcon sx={{ fontSize: '1rem' }} />
                            {CONTACT_EMAIL}
                        </MuiLink>
                    </Stack>
                    <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                        {t('footer.credits')}{' '}
                        <MuiLink href="https://www.adzuna.com" target="_blank" rel="noopener noreferrer" sx={{ color: 'text.secondary', fontWeight: 600 }}>Adzuna</MuiLink>
                    </Typography>
                </Stack>
            </Container>
        </Box>
    );
}
